import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePicker, TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { EColors2, EFonts } from 'libs/styles/styling';
import styled, { css } from 'styled-components/macro';

interface Props {
    label: string;
    value: string;
    type: 'time' | 'date' | 'datetime';
    disabled?: boolean;
    direction?: 'horizontal' | 'vertical';
    placeholder?: string;
    onChange?: (value: string) => void;
    disabledDate?: (current: dayjs.Dayjs) => boolean;
    className?: string;
}

export const GuideInputDateTime = (props: Props): JSX.Element => {
    const getValues = () => {
        if (props.type === 'datetime') {
            if (!props.value) {
                return { date: null, time: null };
            }
            return {
                date: dayjs.utc(props.value.substring(0, 10), 'YYYY-MM-DD'),
                time: props.value.length > 10 ? dayjs(props.value.substring(11, 16), 'HH:mm') : null
            };
        }
        if (props.type === 'date') {
            return { date: props.value ? dayjs.utc(props.value, 'YYYY-MM-DD') : null };
        }
        if (props.type === 'time') {
            return {
                time: props.value ? dayjs(props.value, 'HH:mm') : null
            };
        }
    };

    const values = getValues();

    const onDateChange = (newValue: Dayjs) => {
        if (!newValue) {
            return props?.onChange('');
        }
        if (props.type === 'datetime' && values.time) {
            return props?.onChange(
                newValue.format('YYYY-MM-DD') + 'T' + values.time.format('HH:mm') + ':00.000Z'
            );
        }
        return props?.onChange(newValue.format('YYYY-MM-DD'));
    };

    const onTimeChange = (newTime: Dayjs) => {
        if (!newTime) {
            if (props.type === 'datetime') {
                return props?.onChange(values.date.format('YYYY-MM-DD'));
            }
            return props?.onChange('');
        }

        if (props.type === 'datetime') {
            return props?.onChange(
                values.date.format('YYYY-MM-DD') + 'T' + newTime.format('HH:mm') + ':00.000Z'
            );
        }
        return props?.onChange(newTime.format('HH:mm'));
    };

    const isTimeDisabled = () => {
        if (props.disabled) {
            return true;
        }
        if (props.type === 'datetime') {
            return !values.date;
        }
        return false;
    };

    return (
        <S_GuideInputDateTime className={props.className}>
            <S_Label $direction={props.direction ?? 'vertical'}>
                <S_LabelText>{props.label}</S_LabelText>
                <S_Pickers>
                    {(props.type === 'datetime' || props.type === 'date') && (
                        <S_DatePicker
                            allowClear={false}
                            value={values.date}
                            disabledDate={props.disabledDate}
                            format={'DD.MM.YYYY'}
                            hideDisabledOptions={true}
                            onChange={onDateChange}
                            onBlur={(e) => {
                                const newValue = dayjs(e.target.value, 'DD.MM.YYYY');
                                onDateChange(newValue.isValid() ? newValue : null);
                            }}
                            placeholder={props.placeholder ?? 'DD.MM.YYYY'}
                        />
                    )}
                    {(props.type === 'datetime' || props.type === 'time') && (
                        <S_TimePicker
                            value={values.time}
                            showNow={false}
                            disabled={isTimeDisabled()}
                            onChange={onTimeChange}
                            format={'HH:mm'}
                            allowClear={props.type === 'datetime'}
                            changeOnBlur
                            onBlur={(e) => {
                                const newValue = dayjs(e.target.value, 'HH:mm');
                                onTimeChange(newValue.isValid() ? newValue : null);
                            }}
                            placeholder={props.placeholder ?? 'HH:mm'}
                        />
                    )}
                    {props.value && (
                        <S_Reset
                            icon={['far', 'xmark']}
                            onClick={() => {
                                props?.onChange('');
                            }}
                        />
                    )}
                </S_Pickers>
            </S_Label>
        </S_GuideInputDateTime>
    );
};

const S_GuideInputDateTime = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const S_Label = styled.div<{ $direction: Props['direction'] }>`
    display: block;
    ${(props) =>
        props.$direction === 'horizontal' &&
        css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
        `}
`;

const S_LabelText = styled.div`
    color: ${EColors2.DARK};
    font-size: 16px;
    margin: 0 0 12px 0;
    font-family: ${EFonts.Medium};
`;

const S_DatePicker = styled(DatePicker)`
    background-color: #fafcfe;
    border: 1px solid #e3e3e3;
    height: 44px;
    line-height: 35px;
    border-radius: 50px;

    &:hover,
    &:focus {
        outline: 0;
    }
`;

const S_TimePicker = styled(TimePicker)`
    background-color: #fafcfe;
    border: 1px solid #e3e3e3;
    height: 44px;
    line-height: 35px;
    border-radius: 50px;
    &:hover,
    &:focus {
        outline: 0;
    }
`;

const S_Reset = styled(FontAwesomeIcon)`
    font-size: 20px;
    color: ${EColors2.DARK};
    cursor: pointer;
`;

const S_Pickers = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 15px;
`;
